import { CODE_SYSTEMS, type TaskCategoryCode } from '@liza/fhir-utils';
import type {
  CodeableConcept,
  Extension,
  PlanDefinitionActionCondition,
  PlanDefinitionActionParticipant,
  RelatedArtifact,
  Timing,
  TriggerDefinition,
} from '@medplum/fhirtypes';
import { PROTOCOL_EXTENSIONS, type ProtocolItemKind } from '../extensions';

/** FHIRPath, the expression language every protocol condition is written in. */
export const FHIRPATH = 'text/fhirpath';

export const itemKind = (kind: ProtocolItemKind): Extension => ({
  url: PROTOCOL_EXTENSIONS.ITEM_KIND,
  valueCode: kind,
});

export const taskCategory = (category: TaskCategoryCode): Extension => ({
  url: PROTOCOL_EXTENSIONS.TASK_CATEGORY,
  valueCode: category,
});

/** A LOINC coding with no display: displays are resolved from the curated maps at create time. */
export const loincCode = (code: string): CodeableConcept[] => [{ coding: [{ system: CODE_SYSTEMS.LOINC, code }] }];

/**
 * A trigger that anchors an action on when a medication in one of these ValueSets was started.
 *
 * `anchorOf` in the planner only reads a trigger when the action carries `timingDuration` — an action
 * using `timingTiming` anchors on its own latest observation instead, and recurs. So a
 * "N weeks after starting the drug" check needs both: the trigger names the drug, the duration says
 * how long after, and the condition is what stops it re-firing once a result lands.
 */
export const medicationAdded = (valueSetUrls: string[]): TriggerDefinition[] => [
  {
    type: 'data-added',
    data: valueSetUrls.map((valueSet) => ({
      type: 'MedicationRequest',
      codeFilter: [{ path: 'medication', valueSet }],
    })),
  },
];

export const patient: PlanDefinitionActionParticipant[] = [{ type: 'patient' }];
export const practitioner: PlanDefinitionActionParticipant[] = [{ type: 'practitioner' }];

/** An applicability condition: the action materializes only when the expression is true. */
export const applicable = (expression: string, description: string): PlanDefinitionActionCondition[] => [
  { kind: 'applicability', expression: { description, language: FHIRPATH, expression } },
];

/** One event every `period` `periodUnit` — the canonical `interval` timing shape. */
export const every = (period: number, periodUnit: 'd' | 'wk' | 'mo' | 'a'): Timing => ({
  repeat: { frequency: 1, period, periodUnit },
});

/** Complete on any N distinct days per week — the canonical `daysPerWeek` shape. */
export const daysPerWeek = (frequency: number): Timing => ({ repeat: { frequency, period: 1, periodUnit: 'wk' } });

export const ADA_2026_URL = 'https://diabetesjournals.org/care/issue/49/Supplement_1';

/** A citation to a numbered recommendation in the ADA Standards of Care. */
export const ada = (recommendation: string): RelatedArtifact[] => [
  { type: 'citation', display: `ADA Standards of Care in Diabetes—2026, ${recommendation}`, url: ADA_2026_URL },
];

/** The `derived-from` artifact naming the ADA Standards as a protocol's source. */
export const ADA_DERIVED_FROM: RelatedArtifact = {
  type: 'derived-from',
  display: 'ADA Standards of Care in Diabetes—2026',
  url: ADA_2026_URL,
};

/**
 * The type 2 diabetes diagnosis codes a protocol answers to. Both systems are listed because a
 * diagnosis reaches the chart in whichever system its source used: our own coding path writes
 * ICD-10-CM, while imported records frequently carry SNOMED alone. Matching one system only would
 * make a protocol invisible on a chart that plainly has the condition (#10129).
 *
 * The ICD-10 code is a *prefix* — `E11` covers `E11.9`, `E11.65` and the rest of the category. The
 * SNOMED code is matched exactly; subtypes are not resolved, so a chart coded only to a descendant of
 * `44054006` is a known miss until descendant resolution lands.
 */
export const T2DM_FOCUS_CODINGS = [
  { system: CODE_SYSTEMS.ICD10_CM, code: 'E11', display: 'Type 2 diabetes mellitus' },
  { system: CODE_SYSTEMS.SNOMED, code: '44054006', display: 'Diabetes mellitus type 2' },
] as const;
